/**
 * ManageAllViewsDialog — admin-only panel for a table's saved views.
 *
 * Capabilities:
 *   • Lists every workspace-scope + user-scope view (including views
 *     other members created) with creator name + scope tag.
 *   • Rename / delete any view.
 *   • Clone a workspace or user view into a target user's personal
 *     views ("Force into their views"), optionally pinning it as
 *     their default.
 *   • Assign a workspace-scope view as the default for a workspace
 *     role (`table_role_defaults[table_key][role_key]`). Users can
 *     still switch views manually — role default only wins at
 *     initial resolve above the workspace default.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Users, Trash2, Send, ShieldCheck, Pencil, X, Loader2 } from "lucide-react";

export default function ManageAllViewsDialog({ open, onClose, state, tableKey }) {
  const { activeWorkspaceId } = useAuth();
  const { listAllViews, updateView, deleteView, cloneViewToUser, cloneViewToRole, setRoleDefault, clearRoleDefault } = state;
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState({});
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [q, setQ] = useState("");

  const reload = async () => {
    setBusy(true);
    const r = await listAllViews();
    setRows(r.views || []);
    setRoleDefaults(r.role_defaults || {});
    setBusy(false);
  };

  useEffect(() => {
    if (!open) return;
    reload();
    // Members for clone-into-user picker
    api.get(`/workspaces/${activeWorkspaceId}/members`).then((r) => {
      setMembers((r.data || []).filter((m) => m.status === "active"));
    }).catch(() => {});
    // Custom roles + built-ins for role-default picker
    api.get(`/workspaces/${activeWorkspaceId}/roles`).then((r) => {
      setRoles(r.data || []);
    }).catch(() => setRoles([]));
  }, [open, activeWorkspaceId, tableKey]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) =>
      (r.name || "").toLowerCase().includes(s) ||
      (r.created_by_name || "").toLowerCase().includes(s) ||
      r.scope.toLowerCase().includes(s));
  }, [rows, q]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[86vh] overflow-hidden flex flex-col" data-testid="mgr-all-views-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-primary" /> Manage all views — <span className="font-mono text-xs">{tableKey}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 mt-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search views, creators, or scope…"
                 className="h-9 rounded-none flex-1" data-testid="mgr-all-views-search" />
          {busy && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </div>

        {/* Role defaults strip */}
        {Object.keys(roleDefaults || {}).length > 0 && (
          <div className="mt-2 px-3 py-1.5 border border-primary/30 bg-primary/5 text-[11px] flex flex-wrap items-center gap-2"
               data-testid="mgr-role-defaults-strip">
            <span className="text-[10px] uppercase tracking-widest text-primary font-semibold">Role defaults:</span>
            {Object.entries(roleDefaults).map(([roleKey, viewId]) => {
              const v = rows.find((r) => r.id === viewId);
              return (
                <span key={roleKey} className="chip bg-primary/10 border-primary/30 text-primary flex items-center gap-1 px-1.5 py-0"
                      data-testid={`mgr-role-default-chip-${roleKey}`}>
                  <span className="font-mono">{roleKey}</span> → {v?.name || viewId?.slice(0, 6)}
                  <button className="ml-1 hover:text-destructive"
                          title="Clear role default"
                          onClick={() => clearRoleDefault(roleKey).then(reload)}
                          data-testid={`mgr-role-clear-${roleKey}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {/* Views list */}
        <div className="flex-1 min-h-0 overflow-auto mt-2 border border-border"
             data-testid="mgr-all-views-list">
          {filtered.length === 0 && !busy && (
            <div className="p-8 text-center text-sm text-muted-foreground">No views match.</div>
          )}
          {filtered.map((v) => (
            <ViewRow key={v.id} view={v} members={members} roles={roles}
                     onRename={async (name) => { await updateView(v.id, { name }); await reload(); }}
                     onDelete={async () => { await deleteView(v.id); await reload(); }}
                     onCloneToUser={async (uid, asDefault) => {
                       await cloneViewToUser(v.id, uid, asDefault);
                       await reload();
                     }}
                     onCloneToRole={async (roleKey, asDefault) => {
                       await cloneViewToRole(v.id, roleKey, asDefault);
                       await reload();
                     }}
                     onSetRoleDefault={async (roleKey) => {
                       await setRoleDefault(v.id, roleKey);
                       await reload();
                     }} />
          ))}
        </div>

        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} data-testid="mgr-all-views-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewRow({ view, members, roles, onRename, onDelete, onCloneToUser, onCloneToRole, onSetRoleDefault }) {
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(view.name);
  const [cloneUser, setCloneUser] = useState("");
  const [cloneAsDefault, setCloneAsDefault] = useState(false);
  const [showClone, setShowClone] = useState(false);
  const [showRole, setShowRole] = useState(false);
  const [roleKey, setRoleKey] = useState("");
  // Iter · Feb 2026 — Force-to-role state (bulk clone into every
  // active member with the picked role).
  const [showBulk, setShowBulk] = useState(false);
  const [bulkRoleKey, setBulkRoleKey] = useState("");
  const [bulkAsDefault, setBulkAsDefault] = useState(false);

  return (
    <div className="grid grid-cols-12 gap-2 items-start px-3 py-2 border-b border-border last:border-b-0 text-sm hover:bg-muted/20"
         data-testid={`mgr-view-row-${view.id}`}>
      <div className="col-span-5 min-w-0">
        {renaming ? (
          <div className="flex gap-1">
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   className="h-8 rounded-none text-sm" autoFocus
                   data-testid={`mgr-view-rename-input-${view.id}`} />
            <Button size="sm" className="h-8 rounded-none"
                    onClick={async () => {
                      if (!name.trim()) { toast.error("Name required"); return; }
                      await onRename(name.trim());
                      setRenaming(false);
                    }}
                    data-testid={`mgr-view-rename-save-${view.id}`}>Save</Button>
            <Button size="sm" variant="ghost" className="h-8 rounded-none"
                    onClick={() => { setRenaming(false); setName(view.name); }}>Cancel</Button>
          </div>
        ) : (
          <>
            <div className="font-semibold truncate" data-testid={`mgr-view-name-${view.id}`}>{view.name}</div>
            <div className="text-[10px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
              <span className="chip text-[9px] px-1 py-0 bg-muted/40 border-border">{view.scope}</span>
              {view.created_by_name && (
                <span title="Creator">by {view.created_by_name}</span>
              )}
            </div>
            {view.description && (
              <div className="text-[10.5px] text-muted-foreground mt-1 line-clamp-2">{view.description}</div>
            )}
          </>
        )}
      </div>
      <div className="col-span-7 flex flex-wrap gap-1 justify-end">
        <Button variant="outline" size="sm" className="h-7 rounded-none text-[11px]"
                onClick={() => setRenaming(true)}
                data-testid={`mgr-view-rename-btn-${view.id}`}>
          <Pencil className="w-3 h-3 mr-1" /> Rename
        </Button>
        <Button variant="outline" size="sm" className="h-7 rounded-none text-[11px]"
                onClick={() => setShowClone((v) => !v)}
                data-testid={`mgr-view-clone-btn-${view.id}`}>
          <Send className="w-3 h-3 mr-1" /> Force to user
        </Button>
        <Button variant="outline" size="sm" className="h-7 rounded-none text-[11px]"
                onClick={() => setShowBulk((v) => !v)}
                data-testid={`mgr-view-bulk-btn-${view.id}`}
                title="Clone this view into every member of the picked role">
          <Users className="w-3 h-3 mr-1" /> Force to role
        </Button>
        {view.scope === "workspace" && (
          <Button variant="outline" size="sm" className="h-7 rounded-none text-[11px]"
                  onClick={() => setShowRole((v) => !v)}
                  data-testid={`mgr-view-role-btn-${view.id}`}>
            <Users className="w-3 h-3 mr-1" /> Default by role
          </Button>
        )}
        <Button variant="outline" size="sm" className="h-7 rounded-none text-[11px] text-destructive hover:bg-destructive/10"
                onClick={() => {
                  if (confirm(`Delete "${view.name}" for ${view.scope === "user" ? "its owner" : "everyone"}?`)) onDelete();
                }}
                data-testid={`mgr-view-delete-btn-${view.id}`}>
          <Trash2 className="w-3 h-3" />
        </Button>

        {showClone && (
          <div className="w-full mt-2 p-2 border border-primary/40 bg-primary/5 flex flex-wrap items-center gap-2"
               data-testid={`mgr-view-clone-panel-${view.id}`}>
            <Label className="text-[10px] uppercase tracking-widest text-primary">Force into user</Label>
            <Select value={cloneUser} onValueChange={setCloneUser}>
              <SelectTrigger className="h-8 rounded-none w-[240px] text-xs" data-testid={`mgr-view-clone-user-${view.id}`}>
                <SelectValue placeholder="Pick a member…" />
              </SelectTrigger>
              <SelectContent>
                {members.map((m) => (
                  <SelectItem key={m.user_id || m.id} value={m.user_id || m.id}>
                    {m.name || m.email} <span className="text-[10px] text-muted-foreground ml-1">({m.role})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="text-[11px] inline-flex items-center gap-1">
              <input type="checkbox" checked={cloneAsDefault}
                     onChange={(e) => setCloneAsDefault(e.target.checked)}
                     data-testid={`mgr-view-clone-asdefault-${view.id}`} />
              Set as their default
            </label>
            <Button size="sm" className="h-8 rounded-none text-[11px]"
                    onClick={async () => {
                      if (!cloneUser) { toast.error("Pick a member"); return; }
                      await onCloneToUser(cloneUser, cloneAsDefault);
                      setShowClone(false); setCloneUser(""); setCloneAsDefault(false);
                    }}
                    data-testid={`mgr-view-clone-apply-${view.id}`}>
              Apply
            </Button>
          </div>
        )}

        {showRole && view.scope === "workspace" && (
          <div className="w-full mt-2 p-2 border border-primary/40 bg-primary/5 flex flex-wrap items-center gap-2"
               data-testid={`mgr-view-role-panel-${view.id}`}>
            <Label className="text-[10px] uppercase tracking-widest text-primary">Default for role</Label>
            <Select value={roleKey} onValueChange={setRoleKey}>
              <SelectTrigger className="h-8 rounded-none w-[240px] text-xs" data-testid={`mgr-view-role-select-${view.id}`}>
                <SelectValue placeholder="Pick a role…" />
              </SelectTrigger>
              <SelectContent>
                {/* Built-in roles */}
                <SelectItem value="owner">owner</SelectItem>
                <SelectItem value="admin">admin</SelectItem>
                <SelectItem value="member">member</SelectItem>
                {/* Custom roles (id-based) */}
                {roles.map((r) => (
                  <SelectItem key={r.id || r.name} value={r.id || r.name}>
                    {r.name}{r.id ? <span className="text-[10px] text-muted-foreground ml-1">custom</span> : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" className="h-8 rounded-none text-[11px]"
                    onClick={async () => {
                      if (!roleKey) { toast.error("Pick a role"); return; }
                      await onSetRoleDefault(roleKey);
                      setShowRole(false); setRoleKey("");
                    }}
                    data-testid={`mgr-view-role-apply-${view.id}`}>
              Apply
            </Button>
          </div>
        )}

        {showBulk && (
          <div className="w-full mt-2 p-2 border border-primary/40 bg-primary/5 flex flex-wrap items-center gap-2"
               data-testid={`mgr-view-bulk-panel-${view.id}`}>
            <Label className="text-[10px] uppercase tracking-widest text-primary">Force to every member of role</Label>
            <Select value={bulkRoleKey} onValueChange={setBulkRoleKey}>
              <SelectTrigger className="h-8 rounded-none w-[240px] text-xs" data-testid={`mgr-view-bulk-role-${view.id}`}>
                <SelectValue placeholder="Pick a role…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">owner</SelectItem>
                <SelectItem value="admin">admin</SelectItem>
                <SelectItem value="member">member</SelectItem>
                {roles.map((r) => (
                  <SelectItem key={r.id || r.name} value={r.id || r.name}>
                    {r.name}{r.id ? <span className="text-[10px] text-muted-foreground ml-1">custom</span> : null}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="text-[11px] inline-flex items-center gap-1">
              <input type="checkbox" checked={bulkAsDefault}
                     onChange={(e) => setBulkAsDefault(e.target.checked)}
                     data-testid={`mgr-view-bulk-asdefault-${view.id}`} />
              Set as their default
            </label>
            <Button size="sm" className="h-8 rounded-none text-[11px]"
                    onClick={async () => {
                      if (!bulkRoleKey) { toast.error("Pick a role"); return; }
                      if (!confirm(`Clone "${view.name}" into every member with role "${bulkRoleKey}"${bulkAsDefault ? " and make it their default" : ""}?`)) return;
                      await onCloneToRole(bulkRoleKey, bulkAsDefault);
                      setShowBulk(false); setBulkRoleKey(""); setBulkAsDefault(false);
                    }}
                    data-testid={`mgr-view-bulk-apply-${view.id}`}>
              Apply
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
